// OAuth login_challenge stash + recovery helpers for the LoginPage.
//
// Why this exists: Kratos can drop a flow's `return_to` mid-chain (OIDC callback,
// stale flow, default_browser_return_url fallback) and dead-end the user on
// "Already signed in" instead of returning them to cockpit. We mirror the
// challenge into sessionStorage on the first SPA mount and replay it through
// our server-side `/login?login_challenge=…` bridge when needed.
//
// TTL = Hydra login_consent_request_lifespan (30m); Kratos flow lifespan is
// irrelevant once we've left its UI.
//
// Server contract: the recovery URL hits the same handler that Hydra calls
// after `/oauth2/auth`. Renaming the route or the query name on either side
// will silently break recovery — there are unit tests on the URL shape, but
// no end-to-end guard; treat both as a single contract.
//
// Test setup contract: nothing here may reference `sessionStorage` or
// `window` at module load. The vitest stub installs those globals AFTER the
// ESM imports resolve, so any top-level access would crash before the suite
// even starts.

const STORAGE_KEY = 'paretos:oauth_login_challenge'
const TTL_MS = 30 * 60 * 1000

// MUST match auth-ui server's GET handler in src/server/oauth/routes.ts.
const RECOVERY_PATH = '/login'
const CHALLENGE_PARAM = 'login_challenge'

// Where to send the SPA when there's no stash to recover and we need a
// fresh Kratos flow. Lives here next to RECOVERY_PATH so the two URLs
// the LoginPage redirects to are colocated.
export const KRATOS_LOGIN_FALLBACK = '/self-service/login/browser'

export type RecoveryFrom = 'expired' | 'authenticated' | 'anonymous' | 'passed_challenge'

interface StoredChallenge {
  value: string
  expiresAt: number
}

// Internal/test-only — leading underscore signals "don't import outside this
// module or its tests". `write`/`consume` are the public surface; this is
// exposed only so the tests can introspect state without poking sessionStorage.
export const _peekStoredChallenge = (): StoredChallenge | null => {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY)
    if (!raw) {
      return null
    }
    const parsed = JSON.parse(raw) as Partial<StoredChallenge>
    if (typeof parsed.value !== 'string' || typeof parsed.expiresAt !== 'number') {
      return null
    }
    if (Date.now() > parsed.expiresAt) {
      // Don't leave dead entries lying around — keeps peek/consume cheap and
      // avoids surprises for any future code that reads the key directly.
      try {
        sessionStorage.removeItem(STORAGE_KEY)
      } catch {
        /* non-fatal */
      }
      return null
    }
    return { value: parsed.value, expiresAt: parsed.expiresAt }
  } catch {
    return null
  }
}

// Hard deadline from first observation — don't slide TTL on re-mount.
export const writeStoredChallenge = (value: string): void => {
  try {
    const existing = _peekStoredChallenge()
    if (existing && existing.value === value) {
      return
    }
    const entry: StoredChallenge = { value, expiresAt: Date.now() + TTL_MS }
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(entry))
  } catch {
    // sessionStorage unavailable (privacy mode, quota) — non-fatal.
  }
}

// Single-use: clears on access so a server-side reject can't loop us back here.
export const consumeStoredChallenge = (): string | null => {
  const entry = _peekStoredChallenge()
  try {
    sessionStorage.removeItem(STORAGE_KEY)
  } catch {
    /* non-fatal */
  }
  return entry?.value ?? null
}

// Explicit clear — used by sign-out and any other place that ends a session
// the SPA owns, so a stash from an earlier OAuth chain can't get replayed
// against a fresh login attempt.
export const clearStoredChallenge = (): void => {
  try {
    sessionStorage.removeItem(STORAGE_KEY)
  } catch {
    /* non-fatal */
  }
}

// `searchParams.get` returns '' for `?login_challenge=` (empty value) — coerce
// to null so the contract is honest and callers can `if (challenge)` safely.
export const extractLoginChallenge = (url: string | undefined): string | null => {
  if (!url) {
    return null
  }
  try {
    const parsed = new URL(url, window.location.origin)
    return parsed.searchParams.get(CHALLENGE_PARAM) || null
  } catch {
    return null
  }
}

export const buildRecoveryUrl = (challenge: string): string =>
  `${RECOVERY_PATH}?${CHALLENGE_PARAM}=${encodeURIComponent(challenge)}`

// Diagnostic for the recovery path. Stays as-is — these redirects are user-
// invisible and need a log line to be detectable in incident triage.
export const logRecovery = (from: RecoveryFrom): void => {
  // eslint-disable-next-line no-console
  console.warn('oauth_challenge_recovered', { from })
}

// Tab-scoped fuse for the passed_challenge auto-recovery path: if the server
// bridge can't accept the session (cookie attr mismatch, after-login hook gate,
// …) Kratos can bounce the browser back to the same passed_challenge flow.
// Without this fuse, the SPA would loop on `window.location.replace`. One
// attempt per challenge per tab; second hit falls through to the form.
const PASSED_CHALLENGE_GUARD_KEY = 'paretos:passed_challenge_recovered'

export const wasRecoveryAttempted = (challenge: string): boolean => {
  try {
    return sessionStorage.getItem(PASSED_CHALLENGE_GUARD_KEY) === challenge
  } catch {
    // Fail open — better to risk an extra recovery cycle than to skip it
    // (skipping would dead-end the user on the form).
    return false
  }
}

export const markRecoveryAttempted = (challenge: string): void => {
  try {
    sessionStorage.setItem(PASSED_CHALLENGE_GUARD_KEY, challenge)
  } catch {
    /* non-fatal — guard is best-effort */
  }
}
